import config from "../../config.json";
import { format } from "date-fns";
import type { IntlShape } from "react-intl";

// This utility is the compatibility boundary for hundreds of legacy JavaScript
// callers whose API response contracts have not yet been migrated.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type LegacyApiResponse = any;

export type RequestPayload = BodyInit | Record<string, unknown> | null;

interface ApiFieldError {
  field?: string;
  defaultMessage?: string;
}

export interface ApiMessagePayload {
  messageKey?: string;
  errorKey?: string;
  messageArgs?: Record<string, unknown>;
  errorArgs?: Record<string, unknown>;
  message?: string;
  error?: string;
  fieldErrors?: ApiFieldError[];
  [key: string]: unknown;
}

interface UserSessionDetails {
  roles?: string[];
  permissions?: string[];
}

const csrfToken = (): string => localStorage.getItem("CSRF") as string;

/**
 * Get the current locale from localStorage for API requests.
 * Falls back to browser language or 'en' if not set.
 */
const getAcceptLanguageHeader = (): string => {
  return localStorage.getItem("locale") || navigator.language || "en";
};

/**
 * Resolve an API error/success payload to user-facing text. Generalised from
 * the original analyzer-specific helper so any feature that POSTs JSON and
 * needs to surface a backend error message can use the same logic. Recognises
 * (in order): `messageKey`/`errorKey` (+ optional `messageArgs`/`errorArgs`)
 * → React-Intl id; plain `message`/`error` string → verbatim; Spring
 * `BindingResult.fieldErrors` → joined; otherwise the supplied fallback id.
 */
export const resolveApiErrorMessage = (
  intl: IntlShape,
  payload: ApiMessagePayload | null | undefined,
  fallbackId: string,
  fallbackValues: Record<string, unknown> = {},
): string => {
  const key = payload?.messageKey || payload?.errorKey;
  const keyArgs = payload?.messageArgs || payload?.errorArgs || {};
  if (key) {
    return String(intl.formatMessage({ id: key }, keyArgs));
  }
  const text =
    typeof payload?.message === "string"
      ? payload.message
      : typeof payload?.error === "string"
        ? payload.error
        : null;
  if (text) {
    return text;
  }
  if (Array.isArray(payload?.fieldErrors) && payload.fieldErrors.length > 0) {
    return payload.fieldErrors
      .map((fe) =>
        fe.field
          ? `${fe.field}: ${fe.defaultMessage || ""}`
          : fe.defaultMessage,
      )
      .filter(Boolean)
      .join("; ");
  }
  return String(intl.formatMessage({ id: fallbackId }, fallbackValues));
};

const handleSessionError = (response: Response): Response => {
  if (response.status === 403) {
    response
      .clone()
      .json()
      .then((body: ApiMessagePayload) => {
        if (body && body.message && body.message.includes("CSRF")) {
          alert(
            "Your session has expired. The page will reload so you can continue.",
          );
          window.location.reload();
        }
      })
      .catch(() => undefined);
  }
  return response;
};

/**
 * A write that "succeeds" with a page instead of an API answer: a redirect to
 * the login page, or an HTML body from a REST endpoint (which only ever answers
 * JSON or nothing). That is what a proxy or SSO layer hands back once the
 * session has ended, and it means nothing was saved (OGC-1461).
 */
const answeredWithLoginPage = (
  response: Response,
  endPoint: string,
): boolean => {
  if (!response.ok) {
    return false;
  }
  if (response.redirected && /login/i.test(response.url || "")) {
    return true;
  }
  const contentType = response.headers?.get("content-type") || "";
  return endPoint.includes("/rest/") && contentType.includes("text/html");
};

/** Reports an ended session as the 401 it really is. */
const SESSION_ENDED_STATUS = 401;

const DATE_FMT = "yyyy-MM-dd";

/**
 * Format a Date as a local `yyyy-MM-dd` string. Unlike `Date.toISOString()`,
 * this reads the browser's LOCAL date components, so a date-only value picked in
 * a UTC+ timezone is not rolled back a day when sent to the server. Non-Date
 * input is returned as-is (or "" for null/undefined).
 */
export const toLocalIsoDate = (d: Date | string | null | undefined): string =>
  !(d instanceof Date)
    ? d || ""
    : isNaN(d.getTime())
      ? ""
      : format(d, DATE_FMT);

/**
 * Strict parser for a date picker with dateFormat "Y-m-d": a real `yyyy-MM-dd`
 * becomes that local date; anything else is refused (false). Flatpickr's own
 * parser turns text in another shape into 1 January, which was then saved.
 */
export const parseIsoDate = (text: string | null | undefined): Date | false => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((text || "").trim());
  if (!match) {
    return false;
  }
  const [year, month, day] = match.slice(1).map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day
    ? date
    : false;
};

/**
 * Turn a date string as CustomDatePicker renders it (`MM/dd/yyyy`, or
 * `dd/MM/yyyy` under the French locale) back into the `yyyy-MM-dd` the server
 * reads. Returns "" for anything that is not a three-part date.
 */
export const displayDateToIso = (
  displayed: string | null | undefined,
  dateLocale?: string,
): string => {
  const parts = (displayed || "").split("/");
  if (parts.length !== 3) return "";
  const [month, day] =
    dateLocale === "fr-FR" ? [parts[1], parts[0]] : [parts[0], parts[1]];
  return `${parts[2]}-${month}-${day}`;
};

/**
 * Format a timestamp (epoch millis / ISO string / Date) as local
 * `yyyy-MM-dd HH:mm`, or "—" when absent. Companion to toLocalIsoDate for
 * date-time display columns. (Distinct from the legacy `formatTimestamp`
 * below, which takes Unix SECONDS and renders a UTC AM/PM string.)
 */
export const toLocalIsoDateTime = (
  value: Date | string | number | null | undefined,
): string => {
  const d = value ? new Date(value) : null;
  return d && !isNaN(d.getTime()) ? format(d, `${DATE_FMT} HH:mm`) : "—";
};

/**
 * Render a date-of-record (deadline, due date) as `dd/MM/yyyy`. Such values are
 * stored as an end-of-day timestamp, so reading LOCAL components rolls them to
 * the next day for any browser east of the server; the UTC components give the
 * calendar date that was actually entered. Returns "" for absent values.
 */
export const formatDateOnly = (
  value: Date | string | number | null | undefined,
): string => {
  if (!value) {
    return "";
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  const day = String(d.getUTCDate()).padStart(2, "0");
  const month = String(d.getUTCMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${d.getUTCFullYear()}`;
};

export const getFromOpenElisServer = <T = LegacyApiResponse>(
  endPoint: string,
  callback: (response: T | undefined) => void,
  signal: AbortSignal | null = null,
): void => {
  fetch(
    config.serverBaseUrl + endPoint,

    {
      //includes the browser sessionId in the Header for Authentication on the backend server
      credentials: "include",
      method: "GET",
      signal: signal,
      headers: {
        "Accept-Language": getAcceptLanguageHeader(),
      },
    },
  )
    .then((response) => {
      console.debug("checking response");
      // if (response.url.includes("LoginPage")) {
      //     throw "No Login Session";
      // }
      // An error response carries a JSON body too. Handing that body to the
      // caller as if it were data turns a 500 into a render-time crash, so a
      // failed request reports nothing instead.
      if (!response.ok) {
        console.error(`GET ${endPoint} failed: HTTP ${response.status}`);
        callback(undefined);
        return;
      }
      const contentType = response.headers.get("content-type");
      if (contentType && contentType.indexOf("application/json") !== -1) {
        return response.json().then((jsonResp) => {
          callback(jsonResp as T);
        });
      } else {
        callback(undefined);
      }
    })
    .catch((error) => {
      if (error.name === "AbortError" || signal?.aborted) {
        return; // Component is unmounting — don't call callback
      }
      console.error(error);
      callback(undefined);
    });
};

/**
 * Promise-based GET for the query layer.
 *
 * Legacy callers intentionally keep the callback contract above: many of
 * them interpret an application error body as part of their existing flow.
 * Cached reads need a different contract. A non-success HTTP response must
 * reject so TanStack Query can put the screen in its error state instead of
 * treating an error payload as usable data.
 */
export const fetchFromOpenElisServer = async <T>(
  endPoint: string,
  signal?: AbortSignal,
): Promise<T> => {
  const response = await fetch(config.serverBaseUrl + endPoint, {
    credentials: "include",
    method: "GET",
    signal,
    headers: {
      "Accept-Language": getAcceptLanguageHeader(),
    },
  });

  if (!response.ok) {
    throw new Error(`Request failed (${response.status}): ${endPoint}`);
  }

  const contentType = response.headers.get("content-type");
  if (!contentType || !contentType.includes("application/json")) {
    throw new Error(`Expected a JSON response: ${endPoint}`);
  }

  return (await response.json()) as T;
};

export const postToOpenElisServer = <TExtra = unknown>(
  endPoint: string,
  payLoad: RequestPayload,
  callback: (status: number, extraParams?: TExtra) => void,
  extraParams?: TExtra,
): void => {
  fetch(
    config.serverBaseUrl + endPoint,

    {
      //includes the browser sessionId in the Header for Authentication on the backend server
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
        "Accept-Language": getAcceptLanguageHeader(),
      },
      body: payLoad as BodyInit,
    },
  )
    .then(handleSessionError)
    .then((response) =>
      answeredWithLoginPage(response, endPoint)
        ? SESSION_ENDED_STATUS
        : response.status,
    )
    .then((status) => {
      callback(status, extraParams);
    })
    .catch((error) => {
      console.error(error);
      callback(0, extraParams);
    });
};

/**
 * The one body shared by every *FullResponse helper. The callback gets the raw
 * Response, so a 4xx body — a state-machine refusal, a validation message — can
 * be read and shown instead of being flattened into "it failed".
 */
const sendForFullResponse = <TExtra = unknown>(
  method: "POST" | "PUT" | "PATCH" | "DELETE",
  endPoint: string,
  payLoad: RequestPayload | undefined,
  callback: (response: Response | undefined, extraParams?: TExtra) => void,
  extraParams?: TExtra,
): void => {
  fetch(config.serverBaseUrl + endPoint, {
    //includes the browser sessionId in the Header for Authentication on the backend server
    credentials: "include",
    method: method,
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      "Accept-Language": getAcceptLanguageHeader(),
    },
    ...(payLoad === undefined ? {} : { body: payLoad as BodyInit }),
  })
    .then(handleSessionError)
    .then((response) =>
      callback(
        answeredWithLoginPage(response, endPoint)
          ? new Response(null, {
              status: SESSION_ENDED_STATUS,
              statusText: "Session ended",
            })
          : response,
        extraParams,
      ),
    )
    .catch((error) => {
      console.error(error);
      callback(undefined, extraParams);
    });
};

export const postToOpenElisServerFullResponse = <TExtra = unknown>(
  endPoint: string,
  payLoad: RequestPayload,
  callback: (response: Response | undefined, extraParams?: TExtra) => void,
  extraParams?: TExtra,
): void =>
  sendForFullResponse("POST", endPoint, payLoad, callback, extraParams);

export const postToOpenElisServerFormData = <TExtra = unknown>(
  endPoint: string,
  formData: FormData,
  callback: (status: number, extraParams?: TExtra) => void,
  extraParams?: TExtra,
): void => {
  fetch(
    config.serverBaseUrl + endPoint,

    {
      credentials: "include",
      method: "POST",
      headers: {
        "X-CSRF-Token": csrfToken(),
        "Accept-Language": getAcceptLanguageHeader(),
      },
      body: formData,
    },
  )
    .then(handleSessionError)
    .then((response) =>
      answeredWithLoginPage(response, endPoint)
        ? SESSION_ENDED_STATUS
        : response.status,
    )
    .then((status) => {
      callback(status, extraParams);
    })
    .catch((error) => {
      console.error(error);
      callback(0, extraParams);
    });
};

/**
 * Posts a multipart form and hands back the parsed JSON body, for endpoints
 * that answer an upload with a result rather than a bare status (the catalog
 * import's preview and apply). A non-2xx answer still resolves, carrying the
 * status, so callers can show the server's own message.
 */
export const postToOpenElisServerFormDataJsonResponse = <
  T = LegacyApiResponse,
  TExtra = unknown,
>(
  endPoint: string,
  formData: FormData,
  callback: (response: T | undefined, extraParams?: TExtra) => void,
  extraParams?: TExtra,
): void => {
  fetch(config.serverBaseUrl + endPoint, {
    credentials: "include",
    method: "POST",
    headers: {
      "X-CSRF-Token": csrfToken(),
      "Accept-Language": getAcceptLanguageHeader(),
    },
    body: formData,
  })
    .then(handleSessionError)
    .then((response) =>
      response
        .text()
        .then((raw) => (raw ? JSON.parse(raw) : {}))
        .then((parsed) =>
          response.ok
            ? parsed
            : {
                ...parsed,
                status: response.status,
                statusCode: response.status,
              },
        )
        .catch(() => ({
          error: `Request failed (HTTP ${response.status})`,
          status: response.status,
          statusCode: response.status,
        })),
    )
    .then((body) => {
      callback(body as T, extraParams);
    })
    .catch((error) => {
      console.error(error);
      callback(undefined, extraParams);
    });
};

export const postToOpenElisServerJsonResponse = <
  T = LegacyApiResponse,
  TExtra = unknown,
>(
  endPoint: string,
  payLoad: RequestPayload,
  callback: (response: T | undefined, extraParams?: TExtra) => void,
  extraParams?: TExtra,
): void => {
  fetch(
    config.serverBaseUrl + endPoint,

    {
      //includes the browser sessionId in the Header for Authentication on the backend server
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
        "Accept-Language": getAcceptLanguageHeader(),
      },
      body: payLoad as BodyInit,
    },
  )
    .then(handleSessionError)
    .then((response) => {
      // Check if response is ok (status 200-299)
      if (!response.ok) {
        // For error responses, try to parse JSON. If the body is empty
        // (older endpoints return .build() with no payload) the parse will
        // fail — preserve the HTTP status so callers can still distinguish
        // a 409 from a network error.
        return response
          .text()
          .then((raw) => {
            const parsed = raw ? JSON.parse(raw) : {};
            return {
              ...parsed,
              status: response.status,
              statusCode: response.status,
              statusText: response.statusText,
            };
          })
          .catch(() => ({
            error: `Request failed (HTTP ${response.status} ${response.statusText || ""})`,
            message: `Request failed (HTTP ${response.status} ${response.statusText || ""})`,
            status: response.status,
            statusCode: response.status,
            statusText: response.statusText,
          }));
      }
      // For successful responses, parse JSON normally
      return response.json();
    })
    .then((json) => {
      callback(json as T, extraParams);
    })
    .catch((error) => {
      console.error("postToOpenElisServerJsonResponse error:", error);
      // Pass error to callback so calling code can handle it
      callback(
        {
          error: error.message || "Network error",
          message: error.message || "Network error",
          status: 0,
        } as T,
        extraParams,
      );
    });
};

export const postToOpenElisServerForBlob = (
  endPoint: string,
  payLoad: RequestPayload,
  callback: (blob: Blob, response: Response) => void,
  errorCallback?: (error: unknown) => void,
): void => {
  fetch(
    config.serverBaseUrl + endPoint,

    {
      //includes the browser sessionId in the Header for Authentication on the backend server
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
        "Accept-Language": getAcceptLanguageHeader(),
      },
      body: payLoad as BodyInit,
    },
  )
    .then(handleSessionError)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.blob().then((blob) => ({ blob, response }));
    })
    .then(({ blob, response }) => {
      callback(blob, response);
    })
    .catch((error) => {
      console.error(error);
      if (errorCallback) {
        errorCallback(error);
      }
    });
};

export const getFromOpenElisServerForBlob = (
  endPoint: string,
  callback: (blob: Blob, response: Response) => void,
  errorCallback?: (error: Error) => void,
): void => {
  fetch(config.serverBaseUrl + endPoint, {
    credentials: "include",
    headers: {
      "Accept-Language": getAcceptLanguageHeader(),
    },
  })
    .then(handleSessionError)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.blob().then((blob) => ({ blob, response }));
    })
    .then(({ blob, response }) => {
      callback(blob, response);
    })
    .catch((error) => {
      console.error(error);
      if (errorCallback) {
        errorCallback(error);
      }
    });
};

export const postToOpenElisServerForPDF = (
  endPoint: string,
  payLoad: RequestPayload,
  callback: (success: boolean, blob?: Blob) => void,
): void => {
  fetch(
    config.serverBaseUrl + endPoint,

    {
      //includes the browser sessionId in the Header for Authentication on the backend server
      credentials: "include",
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
        "Accept-Language": getAcceptLanguageHeader(),
      },
      body: payLoad as BodyInit,
    },
  )
    .then(handleSessionError)
    .then((response) => response.blob())
    .then((blob) => {
      callback(true, blob);
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.target = "_blank";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    })
    .catch((error) => {
      callback(false);
      console.error(error);
    });
};

export const putToOpenElisServer = (
  endPoint: string,
  payLoad: RequestPayload | undefined,
  callback: (status: number) => void,
): void => {
  // Build the request options
  const options: RequestInit = {
    // includes the browser sessionId in the Header for Authentication on the backend server
    credentials: "include",
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      "Accept-Language": getAcceptLanguageHeader(),
    },
  };

  // Include the body only if payLoad is provided
  if (payLoad) {
    options.body = payLoad as BodyInit;
  }

  fetch(config.serverBaseUrl + endPoint, options)
    .then(handleSessionError)
    .then((response) =>
      answeredWithLoginPage(response, endPoint)
        ? SESSION_ENDED_STATUS
        : response.status,
    )
    .then((status) => {
      callback(status);
    })
    .catch((error) => {
      console.error(error);
      callback(0);
    });
};

export const putToOpenElisServerJsonResponse = <TExtra = unknown>(
  endPoint: string,
  payLoad: RequestPayload,
  callback: (json: any, extraParams?: TExtra) => void,
  extraParams?: TExtra,
): void => {
  fetch(config.serverBaseUrl + endPoint, {
    //includes the browser sessionId in the Header for Authentication on the backend server
    credentials: "include",
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": localStorage.getItem("CSRF"),
      "Accept-Language": getAcceptLanguageHeader(),
    },
    body: payLoad,
  })
    .then(handleSessionError)
    .then((response) => {
      if (!response.ok) {
        return response.json().then((errorJson) => ({
          ...errorJson,
          status: response.status,
          statusCode: response.status,
          statusText: response.statusText,
        }));
      }
      return response.json();
    })
    .then((json) => {
      callback(json, extraParams);
    })
    .catch((error) => {
      console.error("putToOpenElisServerJsonResponse error:", error);
      callback(
        {
          error: error.message || "Network error",
          message: error.message || "Network error",
          status: 0,
        },
        extraParams,
      );
    });
};

export const putToOpenElisServerFullResponse = <TExtra = unknown>(
  endPoint: string,
  payLoad: RequestPayload,
  callback: (response: Response | undefined, extraParams?: TExtra) => void,
  extraParams?: TExtra,
): void => sendForFullResponse("PUT", endPoint, payLoad, callback, extraParams);

/**
 * PATCH counterpart. The JSON-only PATCH variant discards the body on !ok, which
 * is exactly what a state-machine refusal must not do.
 */
export const patchToOpenElisServerFullResponse = <TExtra = unknown>(
  endPoint: string,
  payLoad: RequestPayload,
  callback: (response: Response | undefined, extraParams?: TExtra) => void,
  extraParams?: TExtra,
): void =>
  sendForFullResponse("PATCH", endPoint, payLoad, callback, extraParams);

export const deleteFromOpenElisServer = (
  endPoint: string,
  callback: (status: number) => void,
): void => {
  fetch(config.serverBaseUrl + endPoint, {
    // includes the browser sessionId in the Header for Authentication on the backend server
    credentials: "include",
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      "Accept-Language": getAcceptLanguageHeader(),
    },
  })
    .then(handleSessionError)
    .then((response) =>
      answeredWithLoginPage(response, endPoint)
        ? SESSION_ENDED_STATUS
        : response.status,
    )
    .then((status) => {
      callback(status);
    })
    .catch((error) => {
      console.error(error);
      callback(0);
    });
};

export const deleteFromOpenElisServerFullResponse = <TExtra = unknown>(
  endPoint: string,
  callback: (response: Response | undefined, extraParams?: TExtra) => void,
  extraParams?: TExtra,
): void =>
  sendForFullResponse("DELETE", endPoint, undefined, callback, extraParams);

export const hasRole = (
  userSessionDetails: UserSessionDetails | null | undefined,
  role: string,
): boolean => {
  if (!userSessionDetails || !userSessionDetails.roles) {
    return false;
  }
  return userSessionDetails.roles.includes(role);
};

/** True when the session carries the named permission. */
export const hasPermission = (
  userSessionDetails: UserSessionDetails | null | undefined,
  permission: string | null | undefined,
): boolean =>
  !!permission && !!userSessionDetails?.permissions?.includes(permission);

/**
 * The gate feature entry points use: the named permission, or the global
 * administrator role, which is allowed everything. Server-side @PreAuthorize is
 * still the real check — this only decides whether to show the entry point.
 */
export const hasPermissionOrGlobalAdmin = (
  userSessionDetails: UserSessionDetails | null | undefined,
  permission: string,
): boolean =>
  hasPermission(userSessionDetails, permission) ||
  hasRole(userSessionDetails, Roles.GLOBAL_ADMIN);

// this is complicated to enable it to format "smartly" as a person types
// possible rework could allow it to only format completed numbers

export const getFromOpenElisServerV2 = <T = LegacyApiResponse>(
  url: string,
): Promise<T> => {
  return new Promise<T>((resolve, reject) => {
    // Simulating the original callback-based function
    getFromOpenElisServer(url, (res) => {
      if (res) {
        resolve(res as T);
      } else {
        reject("Failed to fetch Subscription data");
      }
    });
  });
};

export const patchToOpenElisServerJsonResponse = <
  T = LegacyApiResponse,
  TExtra = unknown,
>(
  endPoint: string,
  payLoad: RequestPayload,
  callback: (response: T | undefined, extraParams?: TExtra) => void,
  extraParams?: TExtra,
): void => {
  fetch(
    config.serverBaseUrl + endPoint,

    {
      //includes the browser sessionId in the Header for Authentication on the backend server
      credentials: "include",
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken(),
        "Accept-Language": getAcceptLanguageHeader(),
      },
      body: payLoad as BodyInit,
    },
  )
    .then(handleSessionError)
    .then((response) => {
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return response.json();
    })
    .then((json) => {
      callback(json as T, extraParams);
    })
    .catch((error) => {
      console.error(error);
      callback(undefined, extraParams);
    });
};

// Drops only a numeric analysis suffix (BASE-N), so IH-2-01 stays whole.
export const labNumberForSearch = (
  accessionNumber: string | null | undefined,
): string => (accessionNumber ?? "").trim().replace(/^([^-]*)-\d+$/, "$1");

export const convertAlphaNumLabNumForDisplay = (
  labNumber: string | null | undefined,
): string | null | undefined => {
  if (!labNumber) {
    return labNumber;
  }
  if (labNumber.length > 15) {
    // Longer-than-15 accessions (e.g. 20-char SiteYearNum like
    // DEV01263000000000001) aren't reformatted — they're opaque IDs.
    // Return as-is without warning; legacy dashed formatting below is only
    // for the old 12-char Tacoma-style lab numbers.
    return labNumber;
  }
  //if dash made it into value, then it's part of the analysis number, not the base lab number
  const labNumberParts = labNumber.split("-");
  const isAnalysisLabNumber = labNumberParts.length > 1;
  let labNumberForDisplay: string;
  //incomplete lab number
  if (labNumberParts[0].length < 8) {
    labNumberForDisplay = labNumberParts[0].slice(0, 2);
    if (labNumberParts[0].length > 2) {
      labNumberForDisplay = labNumberForDisplay + "-";
      labNumberForDisplay = labNumberForDisplay + labNumberParts[0].slice(2);
    }
  } else {
    //possibly complete lab number
    labNumberForDisplay = labNumberParts[0].slice(0, 2) + "-";
    if (labNumberParts[0].length > 8) {
      // lab number contains prefix
      labNumberForDisplay =
        labNumberForDisplay +
        labNumberParts[0].slice(2, labNumberParts[0].length - 6) +
        "-";
    }
    labNumberForDisplay =
      labNumberForDisplay +
      labNumberParts[0].slice(
        labNumberParts[0].length - 6,
        labNumberParts[0].length - 3,
      ) +
      "-";

    labNumberForDisplay =
      labNumberForDisplay +
      labNumberParts[0].slice(labNumberParts[0].length - 3);
  }
  // Re-add every remaining part, not just the first: keeping only one dropped
  // the tail of anything with a second dash in it (an EQA blind code such as
  // IH-2-04 rendered as IH-2, the same for every row on the page).
  if (isAnalysisLabNumber) {
    labNumberForDisplay = [
      labNumberForDisplay,
      ...labNumberParts.slice(1),
    ].join("-");
  }
  return labNumberForDisplay.toUpperCase();
};

export function encodeDate(dateString: string): string {
  if (typeof dateString === "string" && dateString.trim() !== "") {
    return dateString.split("/").map(encodeURIComponent).join("%2F");
  } else {
    return "";
  }
}

export function getDifferenceInDays(date1: string, date2: string): number {
  console.log("secondDate", date2);

  // Function to parse dates in DD/MM/YYYY format
  function parseDate(dateStr: string): Date {
    const [day, month, year] = dateStr.split("/").map(Number);
    return new Date(year, month - 1, day); // Months are 0-based in JavaScript Date
  }

  function correctDate(firstDate: string): string {
    // "08/05/2024" the error is 08 is not day it is month and 05 is day
    const dateParts = firstDate.split("/");
    if (dateParts[0].length === 4) {
      return dateParts[1] + "/" + dateParts[0] + "/" + dateParts[2];
    }
    return firstDate;
  }

  // Convert the date strings to Date objects
  const firstDate = parseDate(correctDate(date1));
  const secondDate = parseDate(correctDate(date2));

  // Calculate the difference in time (milliseconds)
  const timeDifference = secondDate.getTime() - firstDate.getTime();

  // Convert the time difference from milliseconds to days
  const millisecondsPerDay = 1000 * 60 * 60 * 24;
  const dayDifference = timeDifference / millisecondsPerDay;

  // Return the rounded difference in days
  return dayDifference;
}

export function formatTimestamp(timestamp: number): string {
  // Convert the timestamp to milliseconds and create a Date object
  const date = new Date(timestamp * 1000);

  // Extract and format components
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const day = date.getUTCDate();
  const month = date.getUTCMonth() + 1; // Months are zero-based
  const year = date.getUTCFullYear();

  // Determine AM or PM and format hours
  const ampm = hours >= 12 ? "PM" : "AM";
  const formattedHours = (hours % 12 || 12).toString().padStart(2, "0");
  const formattedMinutes = minutes.toString().padStart(2, "0");

  // Format day and month
  const formattedDay = day.toString().padStart(2, "0");
  const formattedMonth = month.toString().padStart(2, "0");

  // Combine and return the formatted string
  return `${formattedHours}:${formattedMinutes} ${ampm}; ${formattedDay}/${formattedMonth}/${year}`;
}

// Helper function to convert a URL-safe base64 string to a Uint8Array
export function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export const Roles = {
  GLOBAL_ADMIN: "Global Administrator",
  USER_ACCOUNT_ADMIN: "User Account Administrator",
  AUDIT_TRAIL: "Audit Trail",
  ANALYSER_IMPORT: "Analyser Import",
  CYTOPATHOLOGIST: "Cytopathologist",
  PATHOLOGIST: "Pathologist",
  RECEPTION: "Reception",
  RESULTS: "Results",
  VALIDATION: "Validation",
  REPORTS: "Reports",
} as const;

/**
 * True when the session holds a qa.* permission, with Global Administrator as
 * the standing fallback. The real gate is @PreAuthorize on the endpoint; this
 * only hides controls from callers who would get a 403 anyway.
 */
export const hasQaPermission = (
  userSessionDetails: { permissions?: string[]; roles?: string[] } | undefined,
  permission: string,
): boolean =>
  !!userSessionDetails?.permissions?.includes(permission) ||
  !!userSessionDetails?.roles?.includes(Roles.GLOBAL_ADMIN);

export const toBase64 = (file: Blob): Promise<string> =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
  });

export const csvCell = (value: unknown): string =>
  `"${String(value ?? "").replace(/"/g, '""')}"`;

export const downloadBlob = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
};
